import React, { useCallback, useEffect, useState } from "react";
import "./Attendance.scss";
import { BASE_URL } from "../../utils/config";
import { adminRequest } from "../../utils/requestMethods";
import NotFound from "../../components/NotFound/NotFound";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Attendance = () => {
  const initialFormData = {
    enrolledProgram: "",
    enrolledSemester: "",
    section: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [program, setProgram] = useState([]);
  const [semester, setSemester] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [activeTab, setActiveTab] = useState("markAttendance");
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [attendanceDate, setAttendanceDate] = useState("");
  const [attendanceDateFrom, setAttendanceDateFrom] = useState("");
  const [attendanceDateTo, setAttendanceDateTo] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [dateNow, setDateNow] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setProgram(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchProgram();
  }, []);

  useEffect(() => {
    if (formData.enrolledProgram) {
      const fetchSemester = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/getSemestersById`,
            { id: formData.enrolledProgram },
          );
          setSemester(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSemester();
    }
  }, [formData.enrolledProgram]);

  useEffect(() => {
    if (formData.enrolledSemester) {
      const fetchSection = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/section/list/by/curriculum/uuid`,
            { uuid: formData.enrolledSemester },
          );
          setSections(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSection();
    }
  }, [formData.enrolledSemester]);

  useEffect(() => {
    if (formData.enrolledSemester) {
      const fetchSubjects = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/list/subject`,
            { uuid: formData.enrolledSemester },
          );
          setSubjects(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSubjects();
    }
  }, [formData.enrolledSemester]);

  useEffect(() => {
    if (formData.section) {
      const fetchStudentBySections = async () => {
        try {
          const response = await adminRequest.post(`${BASE_URL}/student/list`, {
            uuid: formData.section,
            enrolledSemester: formData.enrolledSemester,
            section: formData.section,
          });
          setStudents(response.data.data);
        } catch (error) {
          return <NotFound />;
        }
      };
      fetchStudentBySections();
    }
  }, [formData.section, formData.enrolledSemester]);

  const fetchAttendances = useCallback(async () => {
    const filters = {};
    if (attendanceDate) filters.attendanceDate = attendanceDate;
    if (subjectId) filters.subjectId = subjectId;
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/attendance/list`,
        filters,
      );
      setAttendances(response.data.data);
    } catch (error) {
      return <NotFound />;
    }
  }, [attendanceDate, subjectId]);

  useEffect(() => {
    fetchAttendances();
  }, [fetchAttendances]);

  const fetchAttendanceList = useCallback(async () => {
    const filters = {};
    if (attendanceDateFrom) filters.attendanceDateFrom = attendanceDateFrom;
    if (attendanceDateTo) filters.attendanceDateTo = attendanceDateTo;
    if (subjectId) filters.subjectId = subjectId;
    if (studentId) filters.studentId = studentId;
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/attendance/list/byDate`,
        filters,
      );
      setAttendanceList(response.data.data);
    } catch (error) {
      return <NotFound />;
    }
  }, [attendanceDateFrom, attendanceDateTo, subjectId, studentId]);

  useEffect(() => {
    fetchAttendanceList();
  }, [fetchAttendanceList]);

  const handleSaveAttendance = async () => {
    const attendanceData = students.map((student) => {
      const record = attendanceRecords.find((r) => r.studentId === student.id);
      return {
        studentId: student.id,
        subjectId: selectedSubject,
        attendanceDate: dateNow,
        status: record?.status || "Present",
      };
    });

    const requestBody = {
      attendance: attendanceData,
    };

    try {
      const response = await adminRequest.post(
        `${BASE_URL}/attendance/create`,
        requestBody,
      );
      console.log("Attendance saved:", response.data);
      toast.success(response.data.message);
      fetchAttendances();
      fetchAttendanceList();
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error(error.response.data.message || error.message);
    }
  };

  const downloadAttendance = async () => {
    const filters = {};
    if (attendanceDate) filters.attendanceDate = attendanceDate;
    if (subjectId) filters.subjectId = subjectId;

    try {
      const response = await adminRequest.post(
        `${BASE_URL}/attendance/download`,
        filters,
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "attendance_records.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download the file");
      }
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const generateAttendanceReports = async () => {
    const filters = {};
    if (attendanceDateFrom) filters.attendanceDateFrom = attendanceDateFrom;
    if (attendanceDateTo) filters.attendanceDateTo = attendanceDateTo;
    if (subjectId) filters.subjectId = subjectId;

    try {
      const response = await adminRequest.post(
        `${BASE_URL}/attendance/download/reports`,
        filters,
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );
      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "attendance_records.csv");

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download the file");
      }
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };
  const resetForm = () => {
    setFormData(initialFormData);
    setAttendanceRecords([]);
    setStudents([]);
  };

  const handleResetFilter = () => {
    setFormData(initialFormData);
    setSubjectId("");
    setAttendanceDate("");
    setStudentId("");
  };

  return (
    <div className="attendanceContainer">
      <h1>Attendance</h1>
      <p>Mark and manage student attendance by subject and date.</p>

      <div className="tabs">
        <div
          className={`tabItem ${
            activeTab === "markAttendance" ? "active" : ""
          }`}
          onClick={() => setActiveTab("markAttendance")}
        >
          Mark Attendance
        </div>
        <div
          className={`tabItem ${activeTab === "viewRecords" ? "active" : ""}`}
          onClick={() => setActiveTab("viewRecords")}
        >
          View Records
        </div>
        <div
          className={`tabItem ${activeTab === "reports" ? "active" : ""}`}
          onClick={() => setActiveTab("reports")}
        >
          Reports
        </div>
      </div>

      {activeTab === "markAttendance" && (
        <div className="tabContent">
          <div className="attendanceForm">
            <div className="inputGroup">
              <label>Program</label>
              <select
                name="enrolledProgram"
                value={formData.enrolledProgram}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Program
                </option>
                {program.map((option) => (
                  <option value={option.id} key={option.id}>
                    {option.short_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Semester</label>
              <select
                name="enrolledSemester"
                value={formData.enrolledSemester}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Semester
                </option>
                {semester.map((option) => (
                  <option value={option.uuid} key={option.uuid}>
                    {option.semester}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Section</label>
              <select
                name="section"
                value={formData.section}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Section
                </option>
                {sections.map((option) => (
                  <option value={option.Section.uuid} key={option.Section.uuid}>
                    {option.Section.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Subject</label>
              <select
                name="subject"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select subject
                </option>
                {subjects.map((option) => (
                  <option value={option.id} key={option.uuid}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Attendance Date</label>
              <input
                type="date"
                name="attendanceDate"
                value={dateNow}
                onChange={(e) => setDateNow(e.target.value)}
                required
              />
            </div>
          </div>

          <table className="markAttendanceTable">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => {
                const existingRecord = attendanceRecords.find(
                  (r) => r.studentId === student.id,
                );

                return (
                  <tr key={student.id}>
                    <td>{student.fullName}</td>
                    <td>
                      <select
                        value={existingRecord?.status || "Present"}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setAttendanceRecords((prev) => {
                            const updated = [...prev];
                            const index = updated.findIndex(
                              (r) => r.studentId === student.id,
                            );
                            const newRecord = {
                              studentId: student.id,
                              studentFullName: student.fullName,
                              attendanceDate: dateNow,
                              subject: selectedSubject,
                              status: newStatus,
                            };

                            if (index !== -1) {
                              updated[index] = newRecord;
                            } else {
                              updated.push(newRecord);
                            }

                            return updated;
                          });
                        }}
                      >
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Late">Late</option>
                        <option value="Leave">Leave</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="btnGroup">
            <button
              type="button"
              onClick={resetForm}
              className="resetAttendance"
            >
              Reset
            </button>
            <button
              type="button"
              className="saveAttendance"
              onClick={handleSaveAttendance}
            >
              Save Attendance
            </button>
          </div>
        </div>
      )}

      {activeTab === "viewRecords" && (
        <div className="tabContent">
          <div className="filter">
            <div className="inputGroup">
              <label>Program</label>
              <select
                name="enrolledProgram"
                value={formData.enrolledProgram}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Program
                </option>
                {program.map((option) => (
                  <option value={option.id} key={option.id}>
                    {option.short_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Semester</label>
              <select
                name="enrolledSemester"
                value={formData.enrolledSemester}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Semester
                </option>
                {semester.map((option) => (
                  <option value={option.uuid} key={option.uuid}>
                    {option.semester}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Subject</label>
              <select
                name="subject"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
              >
                <option value="" disabled>
                  Select subject
                </option>
                {subjects.map((option) => (
                  <option value={option.id} key={option.uuid}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Attendance Date</label>
              <input
                type="date"
                name="attendanceDate"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
              />
            </div>
          </div>
          <div className="recordBtnGroup">
            <button onClick={handleResetFilter} className="resetBtn">
              Reset
            </button>
            <button onClick={downloadAttendance} className="downloadBtn">
              Download Records
            </button>
          </div>

          <h2>View Records</h2>
          <table className="attendanceTable">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Subject Name</th>
                <th>Attendance Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {attendances.length === 0 ? (
                <tr>
                  <td colSpan="4">No records found.</td>
                </tr>
              ) : (
                attendances.map((data, index) => (
                  <tr key={index}>
                    <td>{data.studentName}</td>
                    <td>{data.subjectName}</td>
                    <td>{data.attendanceDate}</td>
                    <td>{data.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {activeTab === "reports" && (
        <div className="tabContent">
          <div className="filter">
            <div className="inputGroup">
              <label>Program</label>
              <select
                name="enrolledProgram"
                value={formData.enrolledProgram}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Program
                </option>
                {program.map((option) => (
                  <option value={option.id} key={option.id}>
                    {option.short_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Semester</label>
              <select
                name="enrolledSemester"
                value={formData.enrolledSemester}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Semester
                </option>
                {semester.map((option) => (
                  <option value={option.uuid} key={option.uuid}>
                    {option.semester}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Section</label>
              <select
                name="section"
                value={formData.section}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Section
                </option>
                {sections.map((option) => (
                  <option value={option.Section.uuid} key={option.Section.uuid}>
                    {option.Section.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Subject</label>
              <select
                name="subject"
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
              >
                <option value="" disabled>
                  Select subject
                </option>
                {subjects.map((option) => (
                  <option value={option.id} key={option.uuid}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>Student</label>
              <select
                name="student"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
              >
                <option value="" disabled>
                  Select Student
                </option>
                {students.map((option) => (
                  <option value={option.id} key={option.uuid}>
                    {option.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label>From</label>
              <input
                type="date"
                name="attendanceDateFrom"
                value={attendanceDateFrom}
                onChange={(e) => setAttendanceDateFrom(e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>To</label>
              <input
                type="date"
                name="attendanceDateTo"
                value={attendanceDateTo}
                onChange={(e) => setAttendanceDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="recordBtnGroup">
            <button onClick={handleResetFilter} className="resetBtn">
              Reset
            </button>
            <button onClick={generateAttendanceReports} className="downloadBtn">
              Generate Reports
            </button>
          </div>

          <h2>View Records</h2>
          <table className="attendanceTable">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Subject Name</th>
                <th>Attendance Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {attendanceList.length === 0 ? (
                <tr>
                  <td colSpan="4">No records found.</td>
                </tr>
              ) : (
                attendanceList.map((data, index) => (
                  <tr key={index}>
                    <td>{data.studentName}</td>
                    <td>{data.subjectName}</td>
                    <td>{data.attendanceDate}</td>
                    <td>{data.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      <ToastContainer position="top-center" autoClose={2000} />
    </div>
  );
};

export default Attendance;
